import { Singleton } from '@visiba/instantiation';
import { Http, HttpResponse, HttpOptions, HttpOptionConfiguration } from '@visiba/http';
import { SpaContextService } from './spa_context.service';
import { EnvironmentService } from './environment.service/mod';
import { ApplicationError, ArgumentError } from '@visiba/std';
import { devLocalStorage } from '../dev_local_storage';
import { Logger } from '@modules/logger/logger';
import { UserSettingsService } from './user_settings.service';
import type { VisibaBackendApiResponse } from '@api/ResultDataEnvelopeApiModel';
import { sharedResolution } from '@public/src/shared_resolution';

export interface VisibaEnvelope<T> {
	data: T;
	successful: boolean;
	headers: HttpResponse<T>['headers'];
}

export type VisibaApiPromise<T> = Promise<VisibaEnvelope<T>>;

const mockedResponseData: Record<string, unknown> = Object.create(null);

/**
 * A mocking adapter to intercept response data for a given request. The data
 * will be exhausted after a request has touched it.
 */
export function mockResponse(key: string, data: unknown): void {
	if (mockedResponseData[key]) {
		throw new ArgumentError(`previous "${key}" has never been exhausted, your tests are faulty if this were to happen`);
	}

	mockedResponseData[key] = data;
}

/**
 * The configuration for a request, contains its headers and other useful meta data
 */
export class VisibaOptions extends HttpOptions {
	constructor({ cancelToken, requestConfig, ...rest }: HttpOptionConfiguration) {
		super({
			cancelToken,
			requestConfig: {
				headers: {
					'Content-Type': 'application/json',
					Pragma: 'no-cache',
					'If-Modified-Since': 'Mon, 26 Jul 1997 05:00:00 GMT',
					'Cache-Control': 'no-cache, no-store, must-revalidate',
				},
				...requestConfig,
			},
			...rest,
		});

		const userSettingsService = sharedResolution.resolve(UserSettingsService);
		const config = userSettingsService.getSettings(true);

		const customHeadersBuilder: Record<string, string | boolean> = {
			'X-App-Lang': config?.selectedLanguage ?? '',
		};

		// If this is not passed in any shape or form, backend won't return
		// credentials during development.
		customHeadersBuilder['X-App-WebVersion'] = SpaContextService.webVersion ?? '';

		const requestVerificationToken = devLocalStorage.get('requestVerificationToken');
		const identitySession = devLocalStorage.get('identitySession');
		if (requestVerificationToken) {
			customHeadersBuilder['__RequestVerificationToken'] = requestVerificationToken;
		} else if (process.env.NODE_ENV === 'development' && identitySession) {
			customHeadersBuilder['X-Authorization'] = `ticket ${identitySession}`;
			customHeadersBuilder['X-App-SkipXsfrVerification'] = true;
		}

		this.requestConfig.headers = {
			...this.requestConfig.headers,
			...customHeadersBuilder,
		};
	}
}

function isVisibaEnvelope<T>(response: any): response is { Data: T; Successful: boolean } {
	if (response == null) return false;
	if (typeof response !== 'object') return false;
	if (!('Data' in response)) return false;

	return true;
}

function interceptBeforeGoingToRequester<T>(response: HttpResponse<VisibaBackendApiResponse<T>>): VisibaEnvelope<T> {
	const { headers, config, status } = response;

	if (headers) {
		const webVersion = headers['x-app-webversion'];
		const clientIdentitySession = headers['x-app-clientsession'];

		if (webVersion && SpaContextService.webVersion !== webVersion) {
			if (SpaContextService.webVersion === null) {
				SpaContextService.webVersion = webVersion;
			} else {
				// do something if not matching, currently noop
			}
		}

		if (clientIdentitySession) devLocalStorage.set('identitySession', clientIdentitySession);
	}

	// This we want to remove!!!!!!!!
	if (config.responseType === 'blob') {
		return {
			data: response.data as unknown as T,
			successful: status === 200,
			headers,
		};
	}

	let data: T;
	let successful = true;

	data = response.data as unknown as T;
	// To enforce the removal of the wrapper and migrating to problem details & auto generated clients.
	if (isVisibaEnvelope<typeof data>(data)) {
		successful = data['Successful'];
		data = data['Data'];
	}

	return {
		data,
		successful: successful ?? status === 200,
		headers,
	};
}

class MockClient {
	constructor(private readonly httpClientRef: ReturnType<typeof Http.create>) {
		// Empty
	}

	public get<T>(mockUrl: string, data?: T): VisibaApiPromise<T> {
		return this.sender(this.httpClientRef.get, mockUrl, data, false);
	}

	public post<T>(mockUrl: string, data?: T): VisibaApiPromise<T> {
		return this.sender(this.httpClientRef.post, mockUrl, data, true);
	}

	public put<T>(mockUrl: string, data?: T): VisibaApiPromise<T> {
		return this.sender(this.httpClientRef.put, mockUrl, data, true);
	}

	public delete<T>(mockUrl: string, data?: T): VisibaApiPromise<T> {
		return this.sender(this.httpClientRef.put, mockUrl, data, false);
	}

	private sender<T>(
		fn:
			| MockClient['httpClientRef']['get']
			| MockClient['httpClientRef']['post']
			| MockClient['httpClientRef']['put']
			| MockClient['httpClientRef']['delete'],
		mockUrl: string,
		data: T | undefined,
		hasBody: boolean,
	): VisibaApiPromise<T> {
		const storedData = this.exhaustMockResponse<T>(mockUrl);
		data = storedData !== null ? storedData : data;
		if (data === undefined) {
			throw new ApplicationError('No mock data to be found, its possible that the stored request has already been exhausted');
		}

		const badUrl = '_'; // Incase this would actually try send a real request
		const visibaOption = new VisibaOptions({
			mock: {
				data: this.wrapMockedResponseToBecomeVisibaBackendResponse(data),
			},
		});

		// If it's "post" or "put" a body is required for these function calls
		if (hasBody) {
			return (fn as MockClient['httpClientRef']['post'] | MockClient['httpClientRef']['put'])<VisibaBackendApiResponse<T>>(
				badUrl,
				null,
				visibaOption,
			).then((response) => interceptBeforeGoingToRequester(response));
		}

		return (fn as MockClient['httpClientRef']['get'] | MockClient['httpClientRef']['delete'])<VisibaBackendApiResponse<T>>(
			badUrl,
			visibaOption,
		).then((response) => interceptBeforeGoingToRequester(response));
	}

	private exhaustMockResponse<T>(key: string): T | null {
		if (mockedResponseData[key]) {
			const response = mockedResponseData[key] as T;
			delete mockedResponseData[key];

			return response;
		}

		return null;
	}

	private wrapMockedResponseToBecomeVisibaBackendResponse<T>(data: T): VisibaBackendApiResponse<T> {
		return {
			Data: data,
			ErrorCode: '',
			ErrorMessage: '',
			Successful: true,
		};
	}
}

@Singleton()
export class HttpService {
	private readonly http = Http.create('visiba');

	constructor(private readonly environmentService: EnvironmentService) {
		this.http.setCustomOption(VisibaOptions);
		this.http.setConfig({
			baseURL: this.environmentService.hosting.apiBaseUri,
		});
	}

	public get<T>(...args: ArgumentTypes<HttpService['http']['get']>): VisibaApiPromise<T> {
		Logger.assert(Logger.Level.verbose, `Requests ${this.environmentService.hosting.apiBaseUri}/${args[0]} method = GET`);

		return this.http.get<VisibaBackendApiResponse<T>>(...args).then((response) => interceptBeforeGoingToRequester(response));
	}

	public post<T>(...args: ArgumentTypes<HttpService['http']['post']>): VisibaApiPromise<T> {
		Logger.assert(Logger.Level.verbose, `Requests ${this.environmentService.hosting.apiBaseUri}/${args[0]} method = POST`);

		return this.http.post<VisibaBackendApiResponse<T>>(...args).then((response) => interceptBeforeGoingToRequester(response));
	}

	public put<T>(...args: ArgumentTypes<HttpService['http']['put']>): VisibaApiPromise<T> {
		Logger.assert(Logger.Level.verbose, `Requests ${this.environmentService.hosting.apiBaseUri}/${args[0]} method = PUT`);

		return this.http.put<VisibaBackendApiResponse<T>>(...args).then((response) => interceptBeforeGoingToRequester(response));
	}

	public delete<T>(...args: ArgumentTypes<HttpService['http']['delete']>): VisibaApiPromise<T> {
		Logger.assert(Logger.Level.verbose, `Requests ${this.environmentService.hosting.apiBaseUri}/${args[0]} method = DELETE`);

		return this.http.delete<VisibaBackendApiResponse<T>>(...args).then((response) => interceptBeforeGoingToRequester(response));
	}

	/**
	 * Sets the request into a mocking mode, only use this in development / testing
	 *
	 * The `mockResponse` is a useful utility function that works with combination
	 * with this mode. Use it to create custom response data when testing!
	 * */
	public mock(): MockClient {
		return new MockClient(this.http);
	}
}
